import type { ServiceRequestResult } from '../base-service';
import { ServiceWithSession } from '../base-service';
import type { FormEntryIndex } from '@cognitoforms/types/server-types/forms/model/form-entry-index';
import type { FormsModel } from 'src/framework/forms-model';
import type { EntryViewFilter } from '@cognitoforms/types/server-types/forms/model/entry-view-filter';
import type { EntryView } from '@cognitoforms/types/server-types/forms/model/entry-view';
import type { ObjectLookup } from 'src/util';
import type { DB, Transaction } from 'idb';
import { openDb } from 'idb';
import type { Order } from '@cognitoforms/types/server-types/payment/model/order';
import { IndexType } from '@cognitoforms/types/server-types/forms/model/index-type';
import type { FormSession } from '../form-session';
import type { EntryData, EntityJson, UserSpecificViewSettings } from './entry-view-types';
import { Deferred } from 'src/util/deferred';
import { getLookupManager } from 'src/framework/model/extensions/lookup-field/lookup-manager';
import { getCascadeFilterManager } from 'src/framework/model/extensions/lookup-field/cascade-filter-manager';
import type Log from 'src/framework/logging/log';
import { isEncryptedRecord } from './encryption';
import type * as Encryption from './encryption';
import type { EntryKey } from '@cognitoforms/types/server-types/forms/services/entry-key';
import type { FormEntry } from '@cognitoforms/types/server-types/forms/model/form-entry';
import type { FallbackEntryLookupRequest } from '@cognitoforms/types/server-types/services/controllers/entry-lookup-controller+-fallback-entry-lookup-request';
import type { EntryLookupViewModel } from '@cognitoforms/types/server-types/services/controllers/entry-lookup-view-model';
import type { EntityOfType } from '@cognitoforms/model.js';
import type { EntryLookupKey } from '@cognitoforms/types/server-types/services/controllers/entry-lookup-key';
import { getWithRetry } from 'src/util/http-retry';
import { cloneEntryView, getFilterCacheKey, getKeyword, getServerCriteriaSignature } from './entry-view-util';

/**
 * Controls how often a batch of entries are fetched.
 */
const LOOKUP_BATCH_TIME = 1;
const NONLOOKUP_BATCH_TIME = 225;

/**
 * Controls how many entries can be loaded by a single network request when loading non-lookup entries.
 */
const BATCH_SIZE = 100;

/**
 * Controls how many requests for entry batches can be made for a particular view.
 */
const MAX_CONCURRENT_BATCH_REQUESTS = 3;

export type LoadEntryOptions = {
	isV2Lookup?: boolean;
	fieldPath?: string;
	containingFormId?: string;
	referencesHash?: string;
};

type LoadEntryDataBatch = {
	id: string;
	entries: Map<string, EntryKey | EntryLookupKey>;
	request: Promise<Map<string, EntryData>>;
}

type LookupEntriesResponse = {
	entries: EntryData[];
	shouldRetry: EntryLookupKey[];
};

type V2LookupEntriesResponse = {
	entries: LookupData[];
	shouldRetry: EntryLookupKey[];
};

export type LookupData = {
	__type: 'LookupData';
	$etag: string;
	Id: string;
	[key: string]: any;
};

type ReferenceCacheRecord = {
	Id: string; // Cache key format: {FormId}|{FieldPath}|{SourceEntryId}
	$etag: string;
	data: LookupData;
	secondaryEtags?: Record<string, string>;
	referencesHash: string;
};

type FormEntryIndexRecord = {
	Id: string;
	entries: FormEntryIndex[];
	timestamp: string;
}

type TemporaryEntryViewResponse = {
	TemporaryViewId: string;
	Token: string;
	IsUserSpecific: boolean;
};

export type EntryViewFilterResponse = {
	Body: string;
	Exports: object;
	RequiresServerIndexing?: boolean;
};

type ActiveTemporaryEntryView = {
	baseViewId: string;
	temporaryViewId: string;
	token: string;
	criteriaSignature: string;
	keyword: string;
};

export class EntryLookupBatch {
	private _entryKeys: EntryLookupKey[] = [];
	private _viewTokens: Record<string, string> = {};
	private _referenceHashes: Record<string, string> = {};
	private _requestedViewTokens: string[] = [];
	private _request: Promise<EntryData[] | LookupData[]>;
	private _dependencyMap: Record<string, string[]> = {};
	private _rootEntries: Record<string, EntityOfType<FormEntry>> = {};

	useV2Lookups: boolean;

	constructor(request: Promise<EntryData[] | LookupData[]>, viewTokens: Record<string, string>) {
		this._request = request;
		this._viewTokens = viewTokens;
	}

	get idList() {
		return this._entryKeys.map(key => key.EntryId);
	}

	get entryLookupViewModel(): EntryLookupViewModel & { UseV2Lookups: boolean } {
		const entryKeys: EntryLookupKey[] = this._entryKeys.map(entryKey => ({
			...entryKey
		}));

		return {
			Tokens: this._requestedViewTokens,
			Entries: entryKeys,
			UseV2Lookups: this.useV2Lookups
		};
	}

	addEntryIds(view: string, ids: string[], options: LoadEntryOptions, rootEntry: EntityOfType<FormEntry> | null) {
		const viewToken = this._viewTokens[view];
		if (!viewToken || ids.length === 0)
			return;

		// Get or add view token index
		let viewTokenIndex = this._requestedViewTokens.indexOf(viewToken);
		if (viewTokenIndex === -1) {
			viewTokenIndex = this._requestedViewTokens.length;
			this._requestedViewTokens.push(viewToken);
		}

		for (const id of ids) {
			// Find existing entry key with matching EntryId, FieldPath, and DependentFormId
			let entryKey = this._entryKeys.find(key => key.EntryId === id
				&& key.DependentFormId === options.containingFormId
				// only differentiate by field path if this is a v2 lookup
				&& (!options.isV2Lookup || key.FieldPath === options.fieldPath));

			if (!entryKey) {
				entryKey = {
					EntryId: id,
					Views: [viewTokenIndex],
					FieldPath: options.fieldPath,
					DependentFormId: options.containingFormId
				} as EntryLookupKey;
				this._entryKeys.push(entryKey);

				if (options.referencesHash !== undefined)
					this._referenceHashes[getReferenceCacheKey(entryKey)] = options.referencesHash;

				if (rootEntry?.meta?.id) {
					this._dependencyMap[rootEntry.meta.id] ??= [];
					this._dependencyMap[rootEntry.meta.id].push(id);

					this._rootEntries[rootEntry.meta.id] = rootEntry;
				}
			}
			else if (!entryKey.Views.includes(viewTokenIndex)) {
				entryKey.Views.push(viewTokenIndex);
			}
		}
	}

	getFallbackBatches(entriesToRetry: EntryLookupKey[]) {
		const retries: Record<string, EntryLookupKey[]> = {};
		for (const key of entriesToRetry) {
			for (const [rootEntryId, dependentEntryIds] of Object.entries(this._dependencyMap)) {
				if (dependentEntryIds.includes(key.EntryId)) {
					retries[rootEntryId] ??= [];
					retries[rootEntryId].push(key);
					break;
				}
			}
		}

		return Object.entries(retries).map(([rootEntryId, dependentEntryIds]) => ({
			rootEntry: this._rootEntries[rootEntryId],
			dependentEntryIds
		}));
	}

	getEntries(ids: string[]): Promise<EntryData[] | LookupData[]> {
		return this._request.then(entries => {
			const relevantIds = new Set(ids);
			return entries.filter(e => relevantIds.has(e.Id)) as (EntryData[] | LookupData[]);
		});
	}

	get referenceHashes(): Record<string, string> {
		return { ...this._referenceHashes };
	}
}

let _id = 0;
let crypto: typeof Encryption;
const cryptoKeys: ObjectLookup<CryptoKey> = {};

type DbStoreName = 'FormEntry' | 'FormEntryIndex' | 'ReferenceCache';

export default class EntryViewService extends ServiceWithSession {
	readonly id = _id++;
	readonly model: FormsModel;
	private log: Log;
	private db: Promise<DB> = Promise.resolve(null);
	private readonly sessionViewTokens: Readonly<Record<string, string>>;
	private additionalViewTokens: Record<string, string>;
	private encryptionSeed: ArrayBuffer;

	private loadingPromises = new Set<Promise<void>>();
	private entryLookupBatch: EntryLookupBatch;
	private entryLookupBatchV2: EntryLookupBatch;
	private loadEntryBatches: ObjectLookup<LoadEntryDataBatch> = {};
	private activeTempView: ActiveTemporaryEntryView | null = null;
	private filterResponseCache = new Map<string, Promise<EntryViewFilterResponse>>();

	constructor(model: FormsModel, session: FormSession, log: Log, pseudoLoadDeleted = false, suppressLookupLoadingErrors = false) {
		super(session);
		this.model = model;
		this.log = log;
		this.sessionViewTokens = session.viewTokens;
		const entryTypeNames = model.entryTypeName.split('.');
		const orgName = entryTypeNames[entryTypeNames.length - 2];

		if (session.flags.entryCaching) {
			// Increment the version number when adding/changing table schemas.
			this.openIndexedDB(orgName, model.version + 2);
		}

		model.enableLookups(this, getLookupManager, getCascadeFilterManager, log, pseudoLoadDeleted, suppressLookupLoadingErrors);
	}

	public updateLogger(log: Log) {
		this.log = log;
	}

	get logger() {
		return this.log;
	}

	private get viewTokens() {
		if (!this.additionalViewTokens)
			return this.sessionViewTokens;
		return Object.assign({}, this.sessionViewTokens, this.additionalViewTokens);
	}

	public registerViewToken(viewId: string, token: string) {
		if (!this.additionalViewTokens)
			this.additionalViewTokens = {};
		this.additionalViewTokens[viewId] = token;
	}

	/**
	 * Checks whether the current filter expression must be evaluated by a temporary server index.
	 * The filter translation response is cached by `getFilter` so client-side filtering can reuse it without a second request.
	 */
	async requiresServerEntryFiltering(formId: string, filter: EntryViewFilter): Promise<boolean> {
		return !!filter?.Expression
			&& (await this.getFilter(formId, filter)).RequiresServerIndexing === true;
	}

	getActiveTemporaryViewId(baseViewId: string, view?: EntryView): string | null {
		if (!this.activeTempView || this.activeTempView.baseViewId !== baseViewId)
			return null;

		if (view && this.activeTempView.criteriaSignature !== getServerCriteriaSignature(view))
			return null;

		return this.activeTempView.temporaryViewId;
	}

	async createTemporaryEntryView(baseView: EntryView, view: EntryView): Promise<TemporaryEntryViewResponse> {
		const requestView = cloneEntryView(view);
		if (requestView.Filter)
			requestView.Filter.Keyword = null;

		const criteriaSignature = getServerCriteriaSignature(requestView);
		if (this.activeTempView?.baseViewId === baseView.Id && this.activeTempView.criteriaSignature === criteriaSignature) {
			this.activeTempView.keyword = getKeyword(view);
			return {
				TemporaryViewId: this.activeTempView.temporaryViewId,
				Token: this.activeTempView.token,
				IsUserSpecific: !!view.IsUserSpecific
			};
		}

		const previousTemporaryViewId = this.activeTempView?.baseViewId === baseView.Id
			? this.activeTempView.temporaryViewId
			: null;

		const result = await this.serviceRequest({
			method: 'post',
			endpoint: `svc/forms/${baseView.Form.Id}/entry-views/temporary`,
			data: {
				BaseViewId: baseView.Id,
				PreviousTemporaryViewId: previousTemporaryViewId,
				View: requestView
			}
		});

		if (result.response && result.response.data) {
			const response = result.response.data as TemporaryEntryViewResponse;
			this.registerViewToken(response.TemporaryViewId, response.Token);
			this.activeTempView = {
				baseViewId: baseView.Id,
				temporaryViewId: response.TemporaryViewId,
				token: response.Token,
				criteriaSignature,
				keyword: getKeyword(view)
			};
			return response;
		}

		throw this.parseError(result.error);
	}

	clearActiveTemporaryEntryView(baseViewId: string, formId?: string) {
		if (this.activeTempView?.baseViewId === baseViewId) {
			const temporaryViewId = this.activeTempView.temporaryViewId;
			this.activeTempView = null;

			if (formId) {
				this.serviceRequest({
					method: 'delete',
					endpoint: `svc/forms/${formId}/entry-views/temporary/${temporaryViewId}`
				}).catch(() => {
					// Temporary cleanup is best-effort.
				});
			}
		}
	}

	private async openIndexedDB(orgName: string, version: number) {
		const onFail = (e) => this.log?.warn('Failed to open indexed DB.' + e);
		try {
			this.db = openDb(`Cognito.Forms.${orgName}`, version, upgrade => {
				// Non-destructive addition of new table
				if (upgrade.oldVersion === 13 && upgrade.version === 14) {
					if (!upgrade.objectStoreNames.contains('ReferenceCache'))
						upgrade.createObjectStore('ReferenceCache', { keyPath: 'Id' });
				}
				else {
					if (upgrade.objectStoreNames.contains('FormKey'))
						upgrade.deleteObjectStore('FormKey');
					if (upgrade.objectStoreNames.contains('FormEntry'))
						upgrade.deleteObjectStore('FormEntry');
					if (upgrade.objectStoreNames.contains('FormEntryIndex'))
						upgrade.deleteObjectStore('FormEntryIndex');
					if (upgrade.objectStoreNames.contains('ReferenceCache'))
						upgrade.deleteObjectStore('ReferenceCache');

					upgrade.createObjectStore('FormKey', { keyPath: 'Form' });
					upgrade.createObjectStore('FormEntry', { keyPath: 'Id' });
					upgrade.createObjectStore('FormEntryIndex', { keyPath: 'Id' });
					upgrade.createObjectStore('ReferenceCache', { keyPath: 'Id' });
				}
			}).then(async db => {
				(db as any)._db.onversionchange = (e) => {
					db.close();
					this.openIndexedDB(orgName, e.newVersion);
				};

				if (this.session.encryptionKey) {
					crypto = await import('./encryption');

					this.encryptionSeed = await crypto.isCryptoSupported(this.session.encryptionKey);
					return this.encryptionSeed ? db : null;
				}
				else
					return db;
			}).catch(e => {
				onFail(e);
				return null;
			});
		}
		catch (e) {
			onFail(e);
			this.db = Promise.resolve(null);
		}
	}

	async cacheRecord<T>(formId: string, storeName: DbStoreName, record: T) {
		const db = await this.db;
		if (db) {
			try {
				const keyPath = db.transaction(storeName, 'readonly').objectStore(storeName).keyPath as string;
				const encrypted = { [keyPath]: record[keyPath], ...await this.encrypt(formId, record) };
				return await db.transaction(storeName, 'readwrite').objectStore(storeName).put(encrypted);
			}
			catch (e) {
				this.log.warn(e);
				this.db = Promise.resolve(null);
				return null;
			}
		}
	}

	async loadRecord<T>(formId: string, storeName: DbStoreName, recordKey: any, transaction: Transaction = null): Promise<T> {
		return this.executeWithTracking(async () => {
			const db = await this.db;
			if (db) {
				try {
					if (!transaction)
						transaction = db.transaction(storeName, 'readonly');
					const rawData = await transaction.objectStore(storeName).get(recordKey);
					return await this.decrypt(formId, rawData);
				}
				catch (e) {
					this.log.warn(e);
					this.db = Promise.resolve(null);
					return null;
				}
			}
		});
	}

	private getCacheKey(view: string, userShort: string, roleId: number): string {
		if (!userShort)
			return view;

		return `${view}|${userShort}|${roleId}`;
	}

	async loadCachedIndexes(formId: string, view: string, userSpecificSettings?: UserSpecificViewSettings): Promise<FormEntryIndexRecord> {
		const cachedIndexes = await this.loadRecord<FormEntryIndexRecord>(formId, 'FormEntryIndex', this.getCacheKey(view, userSpecificSettings?.userShort, userSpecificSettings?.roleId));

		// Discard timestamps erroneously set to client-time
		// TODO: Remove when we are confident that all timestamps originating from client are discarded
		if (cachedIndexes?.timestamp?.indexOf('Z') > -1)
			cachedIndexes.timestamp = '';

		return cachedIndexes;
	}

	async cacheIndexes(
		formId: string,
		view: string,
		indexes: FormEntryIndex[],
		timestamp: string,
		userSpecificSettings?: UserSpecificViewSettings
	): Promise<void> {
		await this.cacheRecord<FormEntryIndexRecord>(formId, 'FormEntryIndex', {
			entries: indexes,
			timestamp,
			Id: this.getCacheKey(view, userSpecificSettings?.userShort, userSpecificSettings?.roleId)
		});
	}

	private async encrypt<T>(formId: string, data: T) {
		if (this.encryptionSeed)
			return crypto.encrypt(data, await this.getCryptoKey(formId), this.encryptionSeed) as any;
		return data;
	}

	private async decrypt<T>(seed: string, data: T) {
		if (data && isEncryptedRecord(data)) {
			if (this.encryptionSeed)
				return (await crypto.decrypt(data, await this.getCryptoKey(seed), this.encryptionSeed)) as T;
			else
				return null;
		}
		return data;
	}

	private async getCryptoKey(formId: string) {
		// See if the key has already been loaded
		let key = cryptoKeys[formId];
		try {
			// Otherwise, attempt to load from db
			if (!key) {
				const db = await this.db;
				if (db) {
					const formKey = await db.transaction('FormKey', 'readonly').objectStore('FormKey').get(formId) as any;

					// Key found in database cache
					if (formKey)
						key = formKey.Key;
					// Create and cache a new encryption key
					else {
						key = await window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
						await db.transaction('FormKey', 'readwrite').objectStore('FormKey').put({ Form: formId, Key: key });
					}

					cryptoKeys[formId] = key;
				}
			}
		}
		catch (e) {
			this.log.warn(e);
			this.db = Promise.resolve(null);
		}

		return key;
	}

	private isLookupView(viewId: string) {
		return this.viewTokens[viewId]?.startsWith('l-');
	}

	async getIndex(
		formId: string,
		view: string,
		userSpecificSettings?: UserSpecificViewSettings,
		timestamp?: string,
		progressCallback?: (progress: number, interval: number) => void
	): Promise<FormEntryIndex[]> {
		if (!this.isValidView(view))
			return Promise.resolve([]);

		return this.executeWithTracking(async () => {
			// Prevent parallel index building
			let delay = 100;
			let indexes: FormEntryIndex[];
			let result: ServiceRequestResult;
			let errors = 0;

			while (true) {
				// Allow up to 5 failures before cancelling
				result = await this.serviceRequest({
					method: 'get',
					// lookups should use the public index endpoint, regular views use admin
					endpoint: `forms/${this.isLookupView(view) ? 'public' : 'admin'}/${formId}/entries/index/${this.viewTokens[view]}`,
					params: { timestamp: timestamp, poll: true, roleId: userSpecificSettings?.roleId }
				});

				if (result.error) {
					if (++errors >= 5)
						break;
					continue;
				}

				if (result.response && result.response.data) {
					indexes = result.response.data as FormEntryIndex[];
					const lck = indexes.find(idx => idx.Type === IndexType.Lock);
					if (lck) {
						if (progressCallback) progressCallback(lck.PercentDone * 100, delay);

						await new Promise(resolve => {
							setTimeout(resolve, delay);
						});
						// If this is a checkpoint lock, poll faster
						if (lck.Id)
							delay = 100;
						else
							delay = delay * 2 > 10000 ? 10000 : delay * 2;
						continue;
					}
				}
				if (progressCallback) progressCallback(100, -500);
				break;
			}

			if (indexes) {
				return this.ensureBatchesLoaded(indexes);
			}

			throw this.parseError(result.error);
		});
	}

	async getSpecificIndex(
		entryId: string,
		formId: string,
		view: string,
		userSpecificSettings?: UserSpecificViewSettings,
		timestamp?: string
	): Promise<FormEntryIndex[]> {
		if (!this.isValidView(view))
			return Promise.resolve([]);

		return this.executeWithTracking(async () => {
			// Prevent parallel index building
			let pollCount = 0;
			const delay = 100;
			let indexes: FormEntryIndex[];
			let result: ServiceRequestResult;
			while (pollCount++ < 4) {
				result = await this.serviceRequest({
					method: 'get',
					endpoint: `forms/admin/${formId}/entries/index/${entryId}/${this.viewTokens[view]}`,
					params: { timestamp: timestamp, poll: true, roleId: userSpecificSettings?.roleId }
				});
				if (result.response?.data) {
					const index = result.response.data as FormEntryIndex;
					indexes = index ? [index] : [];
					break;
				}
				else {
					await new Promise(resolve => {
						setTimeout(resolve, delay);
					});
				}
			}
			if (indexes) {
				return this.ensureBatchesLoaded(indexes);
			}

			throw this.parseError(result.error);
		});
	}

	async ensureBatchesLoaded(indexes: FormEntryIndex[]): Promise<FormEntryIndex[]> {
		// Load indexes from file storage
		let batches: FormEntryIndex[] = [];
		let lastCheckpoint = 0;
		for (let i = 0; i < indexes.length; i++) {
			const index = indexes[i];
			if (index.Type === IndexType.Checkpoint) {
				batches = [];
				lastCheckpoint = i;
			}

			if (index.Batch && index.Batch.StorageUrl)
				batches.push(index);
		}

		// Only worry about indexes after the most recent checkpoint
		if (lastCheckpoint)
			indexes = indexes.slice(lastCheckpoint);

		// If indexes need to be fetched from file storage, merge them into the existing array
		const requests = batches.map(batch => getWithRetry<FormEntryIndex[]>(this.client, batch.Batch.StorageUrl, { log: this.log }));
		if (requests.length) {
			const responses = await Promise.all(requests);
			let responseIndex = responses.length - 1;
			for (let insertionIndex = indexes.length - 1; insertionIndex >= 0; insertionIndex--) {
				if (indexes[insertionIndex].Batch && indexes[insertionIndex].Batch.StorageUrl) {
					const toInsert = responses[responseIndex].data;
					for (let batchIndex = toInsert.length - 1; batchIndex >= 0; batchIndex--) {
						indexes.splice(insertionIndex + 1, 0, toInsert[batchIndex]);
					}
					responseIndex--;
				}
			}
		}

		return indexes;
	}

	async getFilter(formId: string, filter: EntryViewFilter): Promise<EntryViewFilterResponse> {
		const key = getFilterCacheKey(formId, filter);
		if (!this.filterResponseCache.has(key)) {
			const request = this.serviceRequest({
				method: 'post',
				endpoint: `svc/forms/${formId}/entry-views/filter`,
				data: filter
			}).then(result => {
				if (result.response && result.response.data)
					return result.response.data;

				throw this.parseError(result.error);
			}).catch(error => {
				this.filterResponseCache.delete(key);
				throw error;
			});

			this.filterResponseCache.set(key, request);
		}

		const cachedRequest = this.filterResponseCache.get(key);
		if (!cachedRequest)
			throw new Error(`Missing cached filter response for key: ${key}`);

		return cachedRequest;
	}

	async lookupReferencedEntries(rootEntry: EntityOfType<FormEntry>, entryKeys: EntryLookupKey[]): Promise<EntryData[] | LookupData[]> {
		return this.executeWithTracking(async () => {
			const ids = entryKeys.map(k => k.EntryId);
			const fetchIds = new Set(ids);

			// Try to load entries from cache
			const cachedEntryData = (await this.getCachedEntries(ids)).filter(e => !!e) as EntryData[];

			// Separate cached entries from uncached
			cachedEntryData.forEach(e => fetchIds.delete(e.Id));

			const operationName = 'Lookup Entries Fallback';
			this.log.startTrackEvent(operationName);

			const result = await this.serviceRequest({
				method: 'post',
				endpoint: 'svc/entry-lookup/fallback',
				data: {
					FormId: (rootEntry as any).$rootEntryForm || rootEntry.Form.Id,
					RootEntryToken: (rootEntry as any).$entryToken,
					RootEntryId: (rootEntry as any).$rootEntryId || rootEntry.Id,
					RootViewTokens: (rootEntry as any).$viewTokens,
					RootViewId: (rootEntry as any).$viewId,
					RootEntryChange: (rootEntry as any).$entryChange,
					LookupEntryKeys: Array.from(fetchIds.values())
						.map(id => ({ EntryId: id } as EntryLookupKey))
						// send etag to see if cached entries are outdated
						.concat(cachedEntryData.map(e => ({ EntryId: e.Id, EntryETag: e.$etag } as EntryLookupKey)))
				} satisfies FallbackEntryLookupRequest
			});

			if (result.response && result.response.data) {
				const { entries } = await this.handleEntryLookupResponse(result, cachedEntryData);
				entries.forEach(e => {
					e['$rootEntryId'] = (rootEntry as any).$rootEntryId || rootEntry.Id;
					e['$rootEntryForm'] = (rootEntry as any).$rootEntryForm || rootEntry.Form.Id;
					e['$entryToken'] = (rootEntry as any).$entryToken;
					e['$viewTokens'] = (rootEntry as any).$viewTokens;
					e['$viewId'] = (rootEntry as any).$viewId;
					e['$entryChange'] = (rootEntry as any).$entryChange;
				});
				const fieldsPerForm = entries.map(e => [e.Form.Id, Object.keys(e).length]);
				this.log.stopTrackEvent(operationName, {
					numSourceFormFields: JSON.stringify(Object.fromEntries(fieldsPerForm)).toString(),
					numberLoaded: fetchIds.size.toString()
				});
				return entries;
			}

			throw this.parseError(result.error);
		});
	}

	async lookupReferencedEntriesV2(rootEntry: EntityOfType<FormEntry>, entryKeys: EntryLookupKey[], referenceHashes: Record<string, string>): Promise<LookupData[]> {
		return this.executeWithTracking(async () => {
			const operationName = 'Lookup Entries Fallback';
			this.log.startTrackEvent(operationName);

			const result = await this.serviceRequest({
				method: 'post',
				endpoint: 'svc/entry-lookup/fallback',
				data: {
					FormId: (rootEntry as any).$rootEntryForm || rootEntry.Form.Id,
					RootEntryToken: (rootEntry as any).$entryToken,
					RootEntryId: (rootEntry as any).$rootEntryId || rootEntry.Id,
					RootViewTokens: (rootEntry as any).$viewTokens,
					RootViewId: (rootEntry as any).$viewId,
					RootEntryChange: (rootEntry as any).$entryChange,
					LookupEntryKeys: entryKeys
				} satisfies FallbackEntryLookupRequest
			});

			if (result.response && result.response.data) {
				const { entries } = await this.handleEntryLookupResponseV2(entryKeys, result, [], referenceHashes);

				entries.forEach(e => {
					e['$rootEntryId'] = (rootEntry as any).$rootEntryId || rootEntry.Id;
					e['$rootEntryForm'] = (rootEntry as any).$rootEntryForm || rootEntry.Form.Id;
					e['$entryToken'] = (rootEntry as any).$entryToken;
					e['$viewTokens'] = (rootEntry as any).$viewTokens;
					e['$viewId'] = (rootEntry as any).$viewId;
					e['$entryChange'] = (rootEntry as any).$entryChange;
				});
				this.log.stopTrackEvent(operationName, {
					v2: 'true',
					numberLoaded: entryKeys.length.toString()
				});
				return entries;
			}

			throw this.parseError(result.error);
		});
	}

	private async handleEntryLookupResponse(result: ServiceRequestResult, cachedEntryData: EntryData[]): Promise<LookupEntriesResponse> {
		const data = result.response.data as { orders?: EntityJson<Order>[]; entries: EntityJson<EntryData>[]; };
		const entries: EntryData[] = [];
		const invalidatedEntryIds = new Set<string>();

		const orders = new Map<string, EntityJson<Order>>((data.orders || []).map(o => [o.json.Id, o]));

		const shouldRetry: EntryLookupKey[] = [];
		for (const entryData of data.entries) {
			if (entryData.json) {
				const entry = entryData.json;
				if (entry.Entry.Order && orders.has(entry.Entry.Order.Id))
					entry.Order = orders.get(entry.Entry.Order.Id).json;

				entry.$etag = entryData.etag;
				entries.push(entry);
				invalidatedEntryIds.add(entry.Id);
			}
			else {
				console.warn('Unable to resolve lookup value:', entryData.id, entryData.visibility);
				invalidatedEntryIds.add(entryData.id);
				if (entryData.visibility === 'NotInView' || entryData.visibility === 'NotAssigned')
					shouldRetry.push({
						EntryId: entryData.id,
						Views: [],
						EntryETag: '',
						FieldPath: '',
						DependentFormId: '',
						SecondaryEtags: {}
					});
			}
		}

		// Cache entries
		await Promise.all(entries.map(e => this.cacheRecord(e.Form.Id, 'FormEntry', e)));

		// Return a combination of entries from server, and cached entries that were not expired
		return {
			entries: entries.concat(cachedEntryData.filter(e => !invalidatedEntryIds.has(e.Id))),
			shouldRetry
		};
	}

	async lookupEntries(view: string, ids: string[], rootEntry: EntityOfType<FormEntry> | null, options: LoadEntryOptions): Promise<EntryData[] | LookupData[]> {
		if (!this.isValidView(view))
			return Promise.resolve([]);

		const isV2Lookup = !!options.isV2Lookup;
		const batchField = isV2Lookup ? 'entryLookupBatchV2' : 'entryLookupBatch';

		if (!this[batchField]) {
			const request = new Deferred<EntryData[] | LookupData[]>();
			const batch = this[batchField] = new EntryLookupBatch(request.promise, this.viewTokens);
			batch.useV2Lookups = isV2Lookup;
			setTimeout(async () => {
				delete this[batchField];
				const { entries, shouldRetry } = await this.lookupEntriesInBatch(batch);
				if (shouldRetry.length > 0) {
					try {
						const fallbackBatches = batch.getFallbackBatches(shouldRetry);
						const fallbackEntries: (EntryData | LookupData)[] = [];
						for (const fallbackBatch of fallbackBatches) {
							const batchEntries = (batch.useV2Lookups
								? await this.lookupReferencedEntriesV2(fallbackBatch.rootEntry, fallbackBatch.dependentEntryIds, batch.referenceHashes)
								: await this.lookupReferencedEntries(fallbackBatch.rootEntry, fallbackBatch.dependentEntryIds));
							fallbackEntries.push(...batchEntries);

							const missingEntries = fallbackBatch.dependentEntryIds.filter(key => !batchEntries.find(e => e.Id === key.EntryId));
							if (missingEntries.length > 0) {
								this.log.trace('Rejected entry lookup fallback request for one or more entries.', {
									Source: 'entry-view-service',
									EntryId: fallbackBatch.rootEntry.Id,
									Unloaded: missingEntries.map(k => k.EntryId).join(',')
								});
							}
						}

						request.resolve(entries.concat(fallbackEntries) as (EntryData[] | LookupData[]));
					}
					catch (e) {
						request.reject(e);
					}
				}
				else
					request.resolve(entries);
			}, LOOKUP_BATCH_TIME);
			this.executeWithTracking(() => request.promise);
		}

		this[batchField].addEntryIds(view, ids, options, rootEntry);

		return this[batchField].getEntries(ids);
	}

	private lookupEntriesInBatch(batch: EntryLookupBatch): Promise<LookupEntriesResponse | V2LookupEntriesResponse> {
		return this.executeWithTracking(async () => {
			const lookupViewModel = batch.entryLookupViewModel;

			if (lookupViewModel.UseV2Lookups) {
				return this.lookupEntriesInBatchV2(batch);
			}

			// Try to load entries from cache
			const cachedEntryData = (await this.getCachedEntries(batch.idList)).filter(e => !!e) as EntryData[];

			// Send etag to see if cached entries are outdated
			for (const e of cachedEntryData) {
				const entryKey = lookupViewModel.Entries.find(ek => ek.EntryId === e.Id);
				entryKey.EntryETag = e.$etag;
			}

			const operationName = 'Lookup Entries in Batch';
			this.log.startTrackEvent(operationName);

			const result = await this.serviceRequest({
				method: 'post',
				endpoint: 'svc/entry-lookup',
				data: lookupViewModel
			});

			if (result.response && result.response.data) {
				const { entries, shouldRetry } = await this.handleEntryLookupResponse(result, cachedEntryData);
				for (const e of entries) {
					const entryKey = lookupViewModel.Entries.find(ek => ek.EntryId === e.Id);
					if (entryKey)
						e['$viewTokens'] = entryKey.Views.map(viewIndex => lookupViewModel.Tokens[viewIndex]);
				}

				const fieldsPerForm = entries.map(e => [e.Form.Id, Object.keys(e).length]);
				this.log.stopTrackEvent(operationName, {
					numSourceFormFields: JSON.stringify(Object.fromEntries(fieldsPerForm)).toString(),
					numberLoaded: lookupViewModel.Entries.length.toString()
				});
				return { entries, shouldRetry };
			}

			throw this.parseError(result.error);
		});
	}

	private lookupEntriesInBatchV2(batch: EntryLookupBatch): Promise<V2LookupEntriesResponse> {
		return this.executeWithTracking(async () => {
			const lookupViewModel = batch.entryLookupViewModel;
			const referenceHashes = batch.referenceHashes;

			// Try to load entries from cache
			const cachedLookupRecords = (await this.getCachedLookupData(lookupViewModel.Entries)).filter(e => !!e);

			// Send etag to see if cached entries are outdated
			for (const record of cachedLookupRecords) {
				const entryKey = lookupViewModel.Entries.find(ek => getReferenceCacheKey(ek) === record.Id);
				entryKey.EntryETag = record.$etag;
				entryKey.SecondaryEtags = record.secondaryEtags;
				// force reload if referenced properties have changed
				if (referenceHashes[record.Id] !== record.referencesHash) {
					delete entryKey.EntryETag;
					delete entryKey.SecondaryEtags;
				}
			}

			const operationName = 'Lookup Entries in Batch';
			this.log.startTrackEvent(operationName);

			const result = await this.serviceRequest({
				method: 'post',
				endpoint: 'svc/entry-lookup',
				data: lookupViewModel
			});

			if (result.response && result.response.data) {
				const { entries, shouldRetry } = await this.handleEntryLookupResponseV2(lookupViewModel.Entries, result, cachedLookupRecords, referenceHashes);

				this.log.stopTrackEvent(operationName, {
					v2: 'true',
					numberLoaded: lookupViewModel.Entries.length.toString()
				});
				return { entries, shouldRetry };
			}

			throw this.parseError(result.error);
		});
	}

	async handleEntryLookupResponseV2(keys: EntryLookupKey[], result: ServiceRequestResult, cachedLookupData: ReferenceCacheRecord[], referenceHashes: Record<string, string>): Promise<{ entries: LookupData[]; shouldRetry: EntryLookupKey[]; }> {
		const data = result.response.data as { entries: EntityJson<LookupData>[]; };
		const lookups: ReferenceCacheRecord[] = [];
		const invalidatedEntryIds = new Set<string>();

		const shouldRetry: EntryLookupKey[] = [];
		for (const entityJson of data.entries) {
			if (entityJson.json) {
				const lookupData = entityJson.json;
				lookupData.$etag = this.generateCompositeEtag(entityJson.etag, entityJson.secondaryEtags);
				lookups.push({
					Id: entityJson.id,
					$etag: entityJson.etag,
					data: lookupData,
					secondaryEtags: entityJson.secondaryEtags,
					referencesHash: referenceHashes[entityJson.id]
				});
				invalidatedEntryIds.add(lookupData.Id);
			}
			else {
				console.warn('Unable to resolve lookup value:', entityJson.id, entityJson.visibility);
				invalidatedEntryIds.add(entityJson.id);
				if (entityJson.visibility === 'NotInView' || entityJson.visibility === 'NotAssigned') {
					const [dependentFormId, path, entryId] = entityJson.id.split('|');
					shouldRetry.push({ EntryId: entryId, DependentFormId: dependentFormId, FieldPath: path } as EntryLookupKey);
				}
			}
		}

		// Cache data
		await Promise.all(lookups
			.map(l => ({ dependentForm: l.Id.split('|')[0], record: l }))
			.map(({ dependentForm, record }) => this.cacheRecord(dependentForm, 'ReferenceCache', record)));

		// Return a combination of entries from server, and cached entries that were not expired
		return {
			entries: lookups.concat(cachedLookupData.filter(e => !invalidatedEntryIds.has(e.data.Id))).map(l => l.data),
			shouldRetry
		};
	}

	private async getCachedEntries(ids: string[]): Promise<EntryData[]> {
		const formId = ids[0].split('-')[0];
		const db = await this.db;
		if (db) {
			try {
				const transaction = (await this.db).transaction('FormEntry', 'readonly');
				return await Promise.all(ids.map(id => this.loadRecord<EntryData>(formId, 'FormEntry', id, transaction)));
			}
			catch (e) {
				this.log.warn(e);
				this.db = Promise.resolve(null);
				return [];
			}
		}
		else
			return [];
	}

	private async getCachedLookupData(keys: EntryLookupKey[]): Promise<ReferenceCacheRecord[]> {
		const db = await this.db;
		if (db) {
			try {
				const transaction = db.transaction('ReferenceCache', 'readonly');
				return await Promise.all(keys.map(key => this.loadRecord<ReferenceCacheRecord>(key.DependentFormId, 'ReferenceCache', getReferenceCacheKey(key), transaction)));
			}
			catch (e) {
				this.log.warn(e);
				this.db = Promise.resolve(null);
				return [];
			}
		}
		else {
			return [];
		}
	}

	private generateCompositeEtag(mainEtag: string, secondaryEtags?: Record<string, string>): string {
		if (!secondaryEtags || Object.keys(secondaryEtags).length === 0) {
			return mainEtag;
		}

		// Sort secondary etags by entry id for deterministic ordering
		// Include both entry ID and etag value: entryId:etag
		const sortedSecondaryPairs = Object.keys(secondaryEtags)
			.sort()
			.map(entryId => `${entryId}:${secondaryEtags[entryId]}`);

		return `${mainEtag}|${sortedSecondaryPairs.join('|')}`;
	}

	async loadEntryData(view: string, options: LoadEntryOptions, ...entries: EntryKey[]): Promise<EntryData[] | LookupData[]> {
		if (this.isLookupView(view))
			return this.lookupEntries(view, entries.map(entryKey => entryKey.EntryId), null, options);

		if (!this.isValidView(view))
			return [];

		let batch = this.loadEntryBatches[view];

		if (!batch) {
			const request = new Deferred();
			batch = { entries: new Map(), request: request.promise, id: view };

			setTimeout(() => {
				delete this.loadEntryBatches[view];
				request.resolve(this.loadEntryDataInBatch(batch)
					.then(entryData => new Map(entryData.map(d => [d.Id, d]))));
			}, NONLOOKUP_BATCH_TIME);

			this.executeWithTracking(() => request.promise);
		}

		this.loadEntryBatches[view] = batch;

		for (const key of entries)
			batch.entries.set(key.EntryId, key);

		const dataMap = await batch.request;
		const entryData = entries.map(e => dataMap.get(e.EntryId));
		for (const entry of entryData)
			(entry as any).$viewTokens = [this.viewTokens[view]];
		return entryData;
	}

	private loadEntryDataInBatch(batch: LoadEntryDataBatch): Promise<EntryData[]> {
		return this.executeWithTracking(async () => {
			const indexMap = batch.entries;

			let cachedEntryData = await this.getCachedEntries(Array.from(indexMap.keys()));

			// Entries with out of date etags need to be reloaded
			cachedEntryData = cachedEntryData.filter((entry: EntryData) => {
				if (entry && entry.$etag === indexMap.get(entry.Id).EntryETag) {
					indexMap.delete(entry.Id);
					return true;
				}
				return false;
			}) as EntryData[];

			// All requested entries were found in cache, no web request needed
			if (indexMap.size === 0)
				return cachedEntryData;

			const loadedData: EntryData[] = [];

			let numRequested = 0;
			const requestQueue = [];
			const keysToLoad = Array.from(indexMap.values());

			while (numRequested < keysToLoad.length) {
				if (requestQueue.length === MAX_CONCURRENT_BATCH_REQUESTS) {
					// wait for a spot in the queue to open up
					await Promise.race(requestQueue);
					continue;
				}

				const currentKeyBatch = keysToLoad.slice(numRequested, (numRequested += BATCH_SIZE));

				const request = (async () => {
					const result = await this.serviceRequest({
						method: 'post',
						endpoint: 'forms/admin/entrybatch',
						data: { Token: this.viewTokens[batch.id], Entries: currentKeyBatch }
					});

					if (result.response && result.response.data) {
						const data = result.response.data as { orders?: Order[], entries: EntryData[] };
						const orders = new Map<string, Order>((data.orders || []).map(o => [o.Id, o]));
						// Cache entries
						await Promise.all(data.entries.map(entry => {
							if (entry.Entry.Order && orders.has(entry.Entry.Order.Id))
								entry.Order = orders.get(entry.Entry.Order.Id);
							entry.$etag = indexMap.get(entry.Id).EntryETag;

							return this.cacheRecord(entry.Form.Id, 'FormEntry', entry);
						}));

						loadedData.push(...data.entries);
					}
					else
						throw this.parseError(result.error);

					requestQueue.splice(requestQueue.indexOf(request), 1);
				})();
				requestQueue.push(request);
			}

			// once we're past the loop, we've requested everything we need, but may still need to wait on the last tasks in the queue
			await Promise.all(requestQueue);

			return loadedData.concat(cachedEntryData);
		});
	}

	private executeWithTracking<T>(action: () => Promise<T>) {
		const task = new Deferred();
		this.loadingPromises.add(task.promise);
		const result = action();
		const cleanupTask = () => {
			this.loadingPromises.delete(task.promise);
			task.resolve();
		};
		result.then(cleanupTask, cleanupTask);
		return result;
	}

	registerTask(task: Promise<void>) {
		const trackingTask = new Deferred<void>();
		this.loadingPromises.add(trackingTask.promise);

		task.then(
			() => trackingTask.resolve(),
			() => trackingTask.resolve()
		).finally(() => {
			this.loadingPromises.delete(trackingTask.promise);
		});
	}

	get loadingComplete(): Promise<void> {
		return new Promise(async resolve => {
			do {
				await new Promise<void>(resolve => setTimeout(resolve));	// allow anything awaiting loading to queue more tasks
				await Promise.all(this.loadingPromises);
				await new Promise<void>(resolve => setTimeout(resolve));	// allow anything awaiting loading to queue more tasks
			} while (this.loadingPromises.size);
			resolve();
		});
	}

	isValidView(viewId: string) {
		return !!this.viewTokens[viewId];
	}
}

function getReferenceCacheKey(key: EntryLookupKey): any {
	return `${key.DependentFormId}|${key.FieldPath}|${key.EntryId}`;
}
